import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  Modal,
  Button,
  ButtonGroup,
} from '../../../../components';
import EditPlanBenefitCategory from './EditPlanBenefitCategory';
import DeletePlanBenefitCategory from './DeletePlanBenefitCategory';
import NewPlanBenefitCategory from './NewPlanBenefitCategory';
import api from '../../../../utils/services';

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function PlanBenefitCategory({ isOpen, onClose }) {
  const [openNewPlanBenefitCategory, setOpenNewPlanBenefitCategory] = useState(false);
  const [benefitCategories, setBenefitCategory] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saveBenefitLoading, setSaveBenefitLoading] = useState(false);

  const [openDeletePlanBenefitCategory, setOpenDeletePlanBenefitCategory] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    setSuccessMessage('');
    setErrorMessage('');
    api.getPlanBenefitCategories()
      .then((response) => {
        if (response) {
          setBenefitCategory(response.sort((a, b) => a.id - b.id));
        }
      })
      .catch(() => {
        setErrorMessage('Error getting plan benefit categories!');
      });
  }, [benefitCategories.length]);


  const handleChange = (event, categoryId) => {
    setSuccessMessage('');
    setErrorMessage('');
    const { value } = event.target;
    const currentBenefitCategory = benefitCategories
      .map(x => (x.id === categoryId ? { ...x, name: value } : x));
    setBenefitCategory(currentBenefitCategory);
  };

  const handleSaveBatchBenefitCategories = () => {
    setSaveBenefitLoading(true);
    api.updateBatchPlanBenefitCategories(benefitCategories)
      .then(() => {
        setSuccessMessage('Benefit categories saved successfully!');
      })
      .catch(() => {
        setErrorMessage('Error saving benefit categories!');
      })
      .finally(() => {
        setSaveBenefitLoading(false);
      });
  };

  const handleNewBenefitCategory = (newCategory) => {
    setBenefitCategory([...benefitCategories, newCategory]);
  };

  const handleOpenDelete = (categoryId) => {
    setDeleteCategoryId(categoryId);
    setOpenDeletePlanBenefitCategory(true);
  };

  const handleDeletePlanBenefitCategory = () => {
    api.deletePlanBenefitCategory(deleteCategoryId)
      .then(() => {
        setBenefitCategory(benefitCategories.filter(x => x.id !== deleteCategoryId));
        setOpenDeletePlanBenefitCategory(false);
      })
      .catch(() => {
        setDeleteError('Error deleting the plan benefit category!');
      });
  };

  const handleClose = () => {
    setOpenNewPlanBenefitCategory(false);
    setOpenDeletePlanBenefitCategory(false);
  };

  const planBenefitCategoriesMarkup = (benefitCategories || []).map(category => (
    <EditPlanBenefitCategory
      key={category.id}
      benefitCategory={category}
      onChange={handleChange}
      onOpenDelete={handleOpenDelete}
    />
  ));

  return (
    <Modal
      title="Benefit Categories"
      isOpen={isOpen}
      onClose={onClose}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Modal.Body>
        <Box height="1em" />
        {errorMessage && <Text color="red">{errorMessage}</Text>}
        {successMessage && <Text color="green">{successMessage}</Text>}
        {benefitCategories && benefitCategories.length > 0 ? (
          <React.Fragment>
            <table style={{ width: '-webkit-fill-available' }}>
              <thead>
                <tr>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>{planBenefitCategoriesMarkup}</tbody>
            </table>
          </React.Fragment>
        ) : (
          <Text>There are no benefit categories, please add one.</Text>
        )}
        <Box height="1em" />

        <NewPlanBenefitCategory
          onBenefitCategoryAdded={handleNewBenefitCategory}
          isNewOpen={openNewPlanBenefitCategory}
          onClose={() => handleClose()}
        />
        <DeletePlanBenefitCategory
          isDeleteOpen={openDeletePlanBenefitCategory}
          deleteHandler={handleDeletePlanBenefitCategory}
          deleteError={deleteError}
          onDeleteClose={() => handleClose()}
        />
        <ButtonGroup>
          <Button onClick={() => setOpenNewPlanBenefitCategory(true)}>
            Add Plan Benefit Category
          </Button>
          <Button
            primary
            onClick={() => handleSaveBatchBenefitCategories()}
            loading={saveBenefitLoading}
          >
            Save Plan Benefit Categories
          </Button>
        </ButtonGroup>
      </Modal.Body>
    </Modal>
  );
}

PlanBenefitCategory.propTypes = propTypes;
